import React, { useEffect, useMemo } from "react";
import { authenticationReducerActions } from "../../_services/store";
import { logout } from "../services/store/authentication.actions";
import styled from "styled-components";
import useEnvironment, { EnvironmentEnum } from "../../EnvironmentUtils";
import { useLocation } from "react-router";
import { useDispatch, useSelector } from "react-redux";
import { getPublicResponseNotification } from "../../notification/store/notification.actions";
import NotificationModal from "../../notification/components/NotificationModal";
import LoginFormV2 from "../../_components/LoginFormV2";
import { IGNORED_REDIRECT_PATHS } from "../../_components/PrivateRoute";

const InternalLoginScreen = () => {
    const authentication = useSelector((state) => state.authentication);
    const userRegistration = useSelector((state) => state.userRegistration);
    const dispatch = useDispatch();
    const location = useLocation();

    const redirectUrl = useMemo(() => {
        const queryParameters = new URLSearchParams(location.search);
        const raw = queryParameters.get("redirect_url") || "/";
        const pathname = raw.split(/[?#]/, 1)[0];
        return IGNORED_REDIRECT_PATHS.includes(raw) || IGNORED_REDIRECT_PATHS.includes(pathname) ? "/" : raw;
    }, [location]);



    useEffect(() => {
        // reset login status
        dispatch(logout());
        dispatch(authenticationReducerActions.loginClearSSOMethod());
        dispatch(authenticationReducerActions.clearResetSuccessMessage());
        if (location && location.search && new URLSearchParams(location.search).has("notificationId")) {
            dispatch(getPublicResponseNotification(new URLSearchParams(location.search).get("notificationId")));
        }
        window.Cello && window.Cello("hide");
    }, [dispatch, location]);

    

    const { environment, color } = useEnvironment();

    return (
        <SplitScreenContainer>
            <NotificationModal />
                        <LeftSection>
                <HeaderText>
                    Das manuelle Buchen<br />
                    überspringen
                </HeaderText>
                <AstronautImage
                    src="/astronaut.svg"
                    alt="Astronaut"
                    className="rounded mx-auto d-block"
                />
                <LegalFooter>
                    <LegalLink
                        href="https://www.pathway-solutions.de/impressum"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        Impressum
                    </LegalLink>
                    <LegalSeparator>•</LegalSeparator>
                    <LegalLink
                        href="https://app.pathway-solutions.de/Nutzungsvereinbarung_Platform.pdf"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        Nutzungsvereinbarung
                    </LegalLink>
                    <LegalSeparator>•</LegalSeparator>
                    <LegalLink
                        href="https://www.pathway-solutions.de/data-privacy-webapp"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        Datenschutz
                    </LegalLink>
                </LegalFooter>
            </LeftSection>

            {/* Right Side - Login Form */}
            <RightSection>
                <LoginFormContainer>
                    {environment === EnvironmentEnum.STAGING || environment === EnvironmentEnum.ITEST ? (
                        <LogoContainer className={color}>
                            <img
                                src="/logo_black.png"
                                alt="Pathway Logo"
                                style={{ maxWidth: "200px", marginBottom: "2rem" }}
                                className="rounded mx-auto d-block"
                            />
                            <EnvironmentWrapper>{environment}</EnvironmentWrapper>
                        </LogoContainer>
                    ) : (
                        <img
                            src="/logo_black.png"
                            alt="Pathway Logo"
                            style={{ maxWidth: "200px", marginBottom: "2rem" }}
                            className="rounded mx-auto d-block"
                        />
                    )}
                    <LoginFormV2
                        authentication={authentication}
                        registration={userRegistration}
                        loginIdentifier="platform"
                        redirectUrl={redirectUrl}
                        showProviderLogin={true}
                    />
                    
                     {authentication.method !== "SSO" && <RegistrationLinkContainer>
                        <RegistrationText>Noch kein Account?</RegistrationText>
                        <RegistrationLink 
                            href="/registration"
                            target="_blank" 
                            rel="noopener noreferrer"
                        >
                            Jetzt registrieren
                        </RegistrationLink>
                    </RegistrationLinkContainer>}
                    <CompanyName>
                        pathway solutions GmbH
                    </CompanyName>
                </LoginFormContainer>
            </RightSection>
        </SplitScreenContainer>
    );
};

export default InternalLoginScreen;

const SplitScreenContainer = styled.div`
    display: flex;
    height: 100vh;
    width: 100vw;
    
    @media (max-width: 768px) {
        flex-direction: column;
        height: auto;
        min-height: 100vh;
    }
`;

const LeftSection = styled.div`
    flex: 1;
    background: #EFFBFF;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 2rem;
    
    @media (max-width: 768px) {
        flex: none;
        padding: 1.5rem 1rem;
        min-height: 40vh;
        justify-content: flex-start;
        order: 2; /* Move to bottom on mobile */
    }
`;

const HeaderText = styled.h2`
    font-family: 'Inter', sans-serif;
    font-size: 28px;
    font-weight: 600;
    text-align: center;
    color: #1f2937;
    margin-bottom: 50px;
    line-height: 1.2;
    
    @media (max-width: 768px) {
        font-size: 22px;
        margin-bottom: 1.5rem;
        margin-top: 1rem;
    }
`;

const RightSection = styled.div`
    flex: 1;
    background: white;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 2rem;
    
    @media (max-width: 768px) {
        flex: none;
        padding: 1rem;
        align-items: flex-start;
        min-height: 60vh;
        order: 1; /* Move to top on mobile */
    }
`;

const LoginFormContainer = styled.div`
    width: 100%;
    max-width: 400px;
    
    h1 {
        text-align: center;
        margin-bottom: 2rem;
    }
    
    @media (max-width: 768px) {
        max-width: 100%;
        padding: 0;
    }
`;

const EnvironmentWrapper = styled.div`
    color: white;
    font-weight: bold;
    font-size: 1.5em;
    text-align: center;
    position: absolute;
    bottom: 10px;
    left: 50%;
    transform: translateX(-50%);
`;

const LogoContainer = styled.div`
    background-color: ${({ color }) => color};
    padding: 10px;
    border-radius: 5px;
    text-align: center;
    position: relative;
    margin-bottom: 20px;
`;

const AstronautImage = styled.img`
    max-width: 60%;
    opacity: 1;
    
    @media (max-width: 768px) {
        max-width: 40%;
        margin-bottom: 1rem;
    }
`;

const RegistrationLinkContainer = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 0.5rem;
    margin-bottom: 2rem;
    
    @media (max-width: 768px) {
        margin-bottom: 1rem;
    }
`;

const RegistrationText = styled.span`
    font-family: 'Inter', sans-serif;
    font-size: 14px;
    color: #6B7280;
`;

const RegistrationLink = styled.a`
    font-family: 'Inter', sans-serif;
    font-size: 14px;
    color: #6c757d;
    text-decoration: underline;
    cursor: pointer;
    transition: color 0.2s ease;
    
    &:hover {
        color: #495057;
        text-decoration: underline;
    }
    
    &:focus {
        outline: none;
        color: #495057;
    }
`;

const LegalFooter = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.75rem;
    margin-top: 2rem;
    padding: 1rem;
    
    @media (max-width: 768px) {
        margin-top: auto;
        padding: 0.5rem;
        gap: 0.5rem;
        flex-wrap: wrap;
    }
`;

const LegalLink = styled.a`
    font-family: 'Inter', sans-serif;
    font-size: 12px;
    color: #6B7280;
    text-decoration: none;
    transition: color 0.2s ease;
    
    &:hover {
        color: #374151;
        text-decoration: underline;
    }
    
    &:focus {
        outline: none;
        color: #374151;
    }
    
    @media (max-width: 768px) {
        font-size: 11px;
    }
`;

const LegalSeparator = styled.span`
    font-family: 'Inter', sans-serif;
    font-size: 12px;
    color: #9CA3AF;
`;

const CompanyName = styled.div`
    font-family: 'Inter', sans-serif;
    font-size: 14px;
    color: #6B7280;
    text-align: center;
    margin-top: 10px;
    margin-bottom: 0px;
    
    @media (max-width: 768px) {
        font-size: 12px;
        margin-bottom: 1rem;
    }
`;
